import Image from "next/legacy/image";
import { useDispatch } from "react-redux";
import { useEffect } from "react";

import { Card, Disclosure, Tag } from "@chef/components";
import {
  resetStandaloneProductModal,
  useProductCategoriesQuery,
  useProductsByCategoriesQuery,
  useProductsByIdsQuery,
} from "@chef/state-management";
import {
  getImageFromVariation,
  getStockData,
  getPriceOfProductByVariation,
  getDiscountedAmount,
  getAttribute,
  getStandaloneProductCategoryIdsFromProductCategories,
  getVariationFromProductsByCategories,
  isDeviationView,
  isHistoricView,
  isDeviationStandaloneBasketVariant,
} from "@chef/state-management/helpers";
import { removeDuplicates } from "@chef/utils/removeDuplicates";
import { isNotEmptyArray } from "@chef/utils/array";
import { useIntercom } from "@chef/hooks";
import {
  useAppSelector,
  useStandaloneBasket,
} from "@chef/state-management/hooks";
import { createAndSendEvent } from "@chef/events";
import { isEqualStrings } from "@chef/utils/equal";

import { CurrencyWrapper } from "../CurrencyWrapper";
import { Modal } from "../modal/Modal";
import { groupNutritionFactsByIngridient } from "./utils";
import { Controls } from "./Controls";

import { intl } from "./StandaloneProductModal.Intl";

export const StandaloneProductModal = () => {
  const dispatch = useDispatch();
  const { open, data } = useAppSelector(
    (state) => state.modal.standaloneProduct,
  );

  useIntercom(!open);

  const standaloneBasketVariant =
    open && isDeviationView(data.view) ? data.basketType : undefined;
  const week = open ? data.week : undefined;
  const year = open ? data.year : undefined;

  const { basket } = useStandaloneBasket({
    variant: standaloneBasketVariant,
    week,
    year,
  });

  const { historicVariation, isLoading: isProductsLoading } =
    useProductsByIdsQuery(
      {
        productIds: open ? [data.productId] : [],
        week: week!,
        year: year!,
      },
      {
        skip: !open || !isHistoricView(data.view),
        selectFromResult: ({ data: result, isLoading }) => ({
          historicVariation:
            data &&
            result?.products
              .find((p) => isEqualStrings(p.productId, data.productId))
              ?.variations.find((v) =>
                isEqualStrings(v.variationId, data.variationId),
              ),
          isLoading,
        }),
      },
    );

  const { data: productCategoriesData, isLoading: isLoadingProductCategories } =
    useProductCategoriesQuery(undefined, {
      skip: !open || isHistoricView(data.view),
    });

  const {
    nonHistoricVariation,
    isLoading: isLoadingStandaloneProductsByCategories,
  } = useProductsByCategoriesQuery(
    {
      categoryIds: getStandaloneProductCategoryIdsFromProductCategories(
        productCategoriesData?.productCategories || [],
      ),
      week: week!,
      year: year!,
    },
    {
      skip: !productCategoriesData,
      selectFromResult: ({ data: result, isLoading }) => ({
        nonHistoricVariation:
          data &&
          getVariationFromProductsByCategories({
            variationId: data.variationId,
            productsByCategories: result?.productsByCategories ?? [],
          }),
        isLoading,
      }),
    },
  );

  const isLoading =
    isLoadingProductCategories ||
    isLoadingStandaloneProductsByCategories ||
    isProductsLoading;

  const variation =
    open && isHistoricView(data.view)
      ? historicVariation
      : nonHistoricVariation;

  useEffect(() => {
    if (!open || !variation) {
      return;
    }

    const price = getPriceOfProductByVariation(variation);

    createAndSendEvent("Product Clicked", {
      affiliation: "Frontend process",
      category: variation.product.productTypeName,
      name: variation.product.name,
      product_id: variation.product.productId,
      variant_id: variation.variationId,
      category_id: variation.product.productTypeId,
      variant: variation.name,
      quantity: 1,
      price,
    });
  }, [open, variation]);

  if (!open || !variation || isLoading) {
    return null;
  }

  const name = variation.name || variation.product.name;

  const description =
    (variation && getAttribute("short_description", variation)) ||
    variation.description;

  const featuredImage = getImageFromVariation({ variation, isFeatured: true });

  const price = getPriceOfProductByVariation(variation);
  const discountedAmount = getDiscountedAmount(variation);
  const showDiscount =
    isDeviationView(data.view) &&
    isDeviationStandaloneBasketVariant(data.basketType);
  const isOnSale = showDiscount && !!discountedAmount;
  const originalPrice = price + discountedAmount;

  const productInBasket = basket?.find((b) =>
    isEqualStrings(b.variationId, variation.variationId),
  );

  const { stockState } = getStockData({
    addedQuantity: productInBasket?.quantity || 0,
    variation,
  });

  const nutritionFacts = groupNutritionFactsByIngridient(
    variation.contents?.nutritionFacts,
  );

  const showDescription = description !== "." && description !== "False";

  return (
    <Modal
      name="standalone-groceries-modal"
      open={open}
      onClose={() => {
        dispatch(resetStandaloneProductModal());
      }}
      animation="fly-up"
      gesture="pull-down-to-close"
    >
      <Card noPadding className="flex flex-col w-full grow md:p-12 !rounded-lg">
        <div className="grid gap-4 md:grid-cols-2 grow">
          <div className="relative">
            <Image
              src={featuredImage}
              alt={name}
              layout="responsive"
              objectFit="cover"
              width="375"
              height="485"
              objectPosition="center center"
              sizes="(min-width: 768px) 50vw, 100vw"
            />

            {isOnSale && (
              <Tag className="absolute top-5 left-5 bg-highlight">
                {intl.OFFERS}
              </Tag>
            )}
          </div>

          <div className="p-6 space-y-4 lg:ml-12 lg:p-0">
            <h2 className="text-lg lg:text-xl">
              <strong>{name}</strong>
            </h2>

            {stockState && (
              <div className="my-2 text-xs text-error lg:hidden">
                {intl.stockState[stockState]}
              </div>
            )}

            <div className="flex flex-row items-center justify-between">
              {!isOnSale ? (
                <p>
                  <strong>
                    <CurrencyWrapper>{price}</CurrencyWrapper>
                  </strong>
                </p>
              ) : (
                <p>
                  <strong>
                    <CurrencyWrapper className="text-discount">
                      {price}
                    </CurrencyWrapper>
                  </strong>

                  <CurrencyWrapper className="ml-2 text-gray-500 line-through">
                    {originalPrice}
                  </CurrencyWrapper>
                </p>
              )}

              {isDeviationView(data.view) && (
                <Controls
                  week={week!}
                  year={year!}
                  variation={variation}
                  basketType={standaloneBasketVariant!}
                />
              )}
            </div>

            {showDescription && (
              <p
                dangerouslySetInnerHTML={{
                  __html: unescape(description),
                }}
              />
            )}

            {variation.contents && (
              <Disclosure
                Title={() => (
                  <strong className="text-base">{intl.CONTENTS}</strong>
                )}
              >
                <dl className="space-y-2">
                  <div className="flex flex-wrap gap-x-1">
                    <dt>
                      <strong>{intl.NETTOWEIGHT}:</strong>
                    </dt>
                    <dd>
                      {variation.contents.nettoWeight}{" "}
                      {variation.contents.unitLabelName}
                    </dd>
                  </div>

                  <div className="flex flex-wrap gap-x-1">
                    <dt>
                      <strong>{intl.INGREDIENTS}:</strong>
                    </dt>
                    <dd>{variation.contents.ingredient}</dd>
                  </div>

                  {isNotEmptyArray(variation.contents.allergies) && (
                    <div className="flex flex-wrap gap-x-1">
                      <dt>
                        <strong>{intl.ALLERGIES}:</strong>
                      </dt>
                      <dd>
                        {removeDuplicates(
                          variation.contents.allergies.map((a) => a.name),
                        ).join(", ")}
                      </dd>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-x-1">
                    <dt>
                      <strong>{intl.SUPPLIER}:</strong>
                    </dt>
                    <dd>{variation.contents.supplierName}</dd>
                  </div>
                  <hr className="my-3" />
                  <div>
                    <dt className="mb-3">
                      <strong>{intl.NUTRITION_FACTS}</strong>
                    </dt>
                    <dd className="flex flex-col gap-2">
                      {nutritionFacts.map(([ingredientName, facts]) => (
                        <div key={ingredientName}>
                          {ingredientName !== "default" && (
                            <strong className="block">{ingredientName}</strong>
                          )}
                          <ul>
                            {facts.map((f) => (
                              <li key={f.name}>
                                {f.name}: {f.value} {f.unit}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </dd>
                  </div>
                </dl>
              </Disclosure>
            )}
          </div>
        </div>
      </Card>
    </Modal>
  );
};
